import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect } from 'react'
import { X, Save, Pen } from 'lucide-react'
import toast from 'react-hot-toast'
import { companyInfoService, type CompanyInfo } from '@/features/configuracion/api/companyInfo.service'
import { formatDate } from '@/shared/lib/dateUtils'
import { EsqueletoTabla } from '@/shared/components/Esqueleto'

type EditField = 'rfc' | 'razonSocial' | null

const CompanyInfoView = () => {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [editField, setEditField] = useState<EditField>(null)
  const [editValue, setEditValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    loadCompanyInfo()
  }, [])

  const loadCompanyInfo = async () => {
    setIsLoading(true)
    try {
      const data = await companyInfoService.get()
      setCompanyInfo(data)
    } catch (error) {
      console.error('Error loading company info:', error)
      toast.error('Error al cargar la información de la empresa')
    } finally {
      setIsLoading(false)
    }
  }

  const openEdit = (field: 'rfc' | 'razonSocial') => {
    setEditField(field)
    setEditValue(field === 'rfc' ? (companyInfo?.rfc ?? '') : (companyInfo?.razonSocial ?? ''))
  }

  const closeEdit = () => {
    setEditField(null)
    setEditValue('')
  }

  const handleSave = async () => {
    if (!editField || !editValue.trim()) return
    setIsSaving(true)
    try {
      const updated = await companyInfoService.update({ [editField]: editValue.trim() })
      setCompanyInfo(updated)
      toast.success('Información actualizada correctamente')
      closeEdit()
    } catch (error) {
      console.error('Error updating company info:', error)
      toast.error('Error al guardar los cambios')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <EsqueletoTabla filas={2} columnas={3} />
      </div>
    )
  }

  const rows: { label: string; field: 'rfc' | 'razonSocial'; value: string }[] = [
    { label: 'RFC de empresa', field: 'rfc', value: companyInfo?.rfc ?? '—' },
    { label: 'Razón social', field: 'razonSocial', value: companyInfo?.razonSocial ?? '—' },
  ]

  const editModalTitle = editField === 'rfc' ? 'Editar RFC' : 'Editar razón social'
  const editInputLabel = editField === 'rfc' ? 'Nuevo RFC' : 'Nueva razón social'
  const editInputPlaceholder = editField === 'rfc' ? 'Ingresa el nuevo RFC' : 'Ingresa la nueva razón social'

  return (
    <div>
      <TituloDePantalla nivel="interna" className="mb-4">RFC y Razón social</TituloDePantalla>

      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[#E5E7EB]">
          <thead className="bg-[#FAF9FF]">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Concepto</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Detalle</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Última edición</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-24">Editar</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#E1E1E1]">
            {rows.map(({ label, field, value }) => (
              <tr key={field} className="hover:bg-[#F4F7FB] transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#1B2128]">{label}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#1B2128]">{value}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(companyInfo?.updatedAt)}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => openEdit(field)}
                    className="text-[#2C3E7C] hover:text-[#1f2d5a] transition-colors"
                  >
                    <Pen className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editField && (
        <CapaDeModal>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">{editModalTitle}</h3>
                <button
                  onClick={closeEdit}
                  className="text-gray-400 hover:text-gray-600 transition-colors bg-[#D9D9D9]/50 rounded-full p-1"
                >
                  <X className="w-5 h-5 text-[#BDBDBD] hover:text-[#1B2128]" />
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {editInputLabel}
                </label>
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder={editInputPlaceholder}
                  className="w-full px-3 py-2.5 border-none rounded-lg bg-[#F6F9FB] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                  autoFocus
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={closeEdit}
                  className={estiloDeBoton('secundario', 'flex-1')}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !editValue.trim()}
                  className={estiloDeBoton('primario', 'flex-1')}
                >
                  {isSaving ? (
                    <div className="girando rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </CapaDeModal>
      )}
    </div>
  )
}

export default CompanyInfoView
